import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatColumnDef, MatTableModule } from '@angular/material/table';
import { instanceOfSortedDataSource } from '../../../collections';
import { ColumnHeaderDirective, ColumnTemplateDirective } from '../../directives';
import { Column } from '../../model/table.config';
import { TABLE_INJECTION_TOKEN, TableComponent } from '../table/table.component';
import { VirtualizedTableComponent } from '../virtualized-table/virtualized-table.component';

import { CommonModule } from '@angular/common';
import { MatSortModule } from '@angular/material/sort';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as deTranslations from '../../i18n/de-lang.json';
import * as enTranslations from '../../i18n/en-lang.json';

@Component({
  selector: 'spx-column',
  templateUrl: './column.component.html',
  imports: [CommonModule, MatTableModule, MatSortModule, TranslateModule],
})
export class ColumnComponent<T> implements OnInit, OnChanges, OnDestroy {
  @Input() name!: string;
  @Input() label?: string | null;
  @Input() filter?: boolean = true;
  @Input() sort?: boolean = true;
  @Input() sortProperty?: string;
  @Input() hidden = false;
  @Input() maxWidth?: string;
  @Input() disableClearSort: boolean = false;

  @ContentChild(ColumnHeaderDirective, { static: true }) columnHeaderTemplate?: ColumnHeaderDirective;
  @ContentChild(ColumnTemplateDirective, { static: true }) columnTemplate?: ColumnTemplateDirective;

  @ViewChild(MatColumnDef) columnDef!: MatColumnDef;

  private column: Column = {} as Column;

  constructor(
    @Optional() @Inject(TABLE_INJECTION_TOKEN) private table: TableComponent<T> | VirtualizedTableComponent<T>,
    private changeDetectorRef: ChangeDetectorRef,
    private translate: TranslateService,
  ) {
    this.translate.setTranslation('en', enTranslations, true);
    this.translate.setTranslation('de', deTranslations, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hidden) {
      if (changes.hidden.currentValue) {
        this.table?.removeDisplayedColumn(this.name);
      } else {
        if (this.columnDef) {
          this.createColumn();
        }
        this.table?.addDisplayedColumn(this.name);
      }
    }
    if (changes.label && !changes.label.firstChange) {
      this.column.label = this.label;
      this.changeDetectorRef.markForCheck();
    }
  }

  ngOnInit(): void {
    if (this.table) {
      this.changeDetectorRef.detectChanges();
      this.createColumn();
      this.table.addColumn(this.column);
      if (this.hidden) {
        this.table.removeDisplayedColumn(this.name);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.table) {
      this.table.removeColumn(this.column);
    }
  }

  public enableSort(): boolean {
    return instanceOfSortedDataSource(this.table.dataSource) && !!this.sort;
  }

  public getSortProperty(): string {
    return this.sortProperty ?? this.name;
  }

  private createColumn(): void {
    if (!this.columnDef) {
      return;
    }
    this.columnDef.name = this.name;
    this.column.columnDef = this.columnDef;
    this.column.name = this.name;
    this.column.label = this.label;
    this.column.filter = this.filter;
    this.column.sort = this.sort;
    this.column.sortProperty = this.sortProperty;
  }

  getElementProperty(element: T, propertyName: string): string | undefined {
    if (element && propertyName.indexOf('.') > 0) {
      const separatorIndex = propertyName.indexOf('.');
      const key = propertyName.slice(0, separatorIndex);
      const rest = propertyName.slice(separatorIndex + 1);
      return this.getElementProperty((element as any)[key], rest);
    } else if (element) {
      return (element as any)[propertyName];
    }
    return undefined;
  }
}
